import { useEffect, useState } from "react";
import { biostimulantProducts, stateNames } from "./constants";

const App = () => {
  const [product, setProduct] = useState(null);
  const [selectedState, setSelectedState] = useState("");

  function toRoman(num) {
    const romans = [
      ["M", 1000],
      ["CM", 900],
      ["D", 500],
      ["CD", 400],
      ["C", 100],
      ["XC", 90],
      ["L", 50],
      ["XL", 40],
      ["X", 10],
      ["IX", 9],
      ["V", 5],
      ["IV", 4],
      ["I", 1],
    ];
    let result = "";
    for (const [letter, value] of romans) {
      while (num >= value) {
        result += letter;
        num -= value;
      }
    }
    return result.toLowerCase(); // lowercase for (i), (ii), (iii)
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const brandParam = params.get("brand");

    if (brandParam) {
      // Decode the URL-encoded brand name (handles spaces and symbols)
      const brandName = decodeURIComponent(brandParam);

      const found = biostimulantProducts.find(
        (item) => item.brandName.toLowerCase() === brandName.toLowerCase()
      );
      setProduct(found);
    }
  }, []);

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-green-50 to-green-100 text-center p-6">
        <div className="bg-white shadow-lg rounded-2xl p-8 max-w-md w-full">
          <h1 className="text-3xl font-bold text-green-700 mb-4">
            Swaroop Biostimulants
          </h1>
          <p className="text-gray-600">
            No brand selected or brand not found.
            <br />
            Try visiting a link like:
          </p>
          <p className="text-green-700 font-semibold mt-2">
            <code>?brand=Humira</code>
          </p>
          <div className="mt-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">
              Available Brands:
            </h2>
            <ul className="space-y-2">
              {biostimulantProducts.map((p) => (
                <li key={p.id}>
                  <a
                    href={`/?brand=${encodeURIComponent(p.brandName)}`}
                    className="text-green-600 hover:text-green-800 font-medium"
                  >
                    {p.brandName}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-10 font-serif">
      <div className="max-w-2xl w-full text-black leading-relaxed">
        <h1 className="text-xl font-bold text-center underline mb-6">
          Product: {product.titleName}
        </h1>

        <p className="mb-3 text-[15px]">
          <span className="font-semibold">1. Gazette Notification Number and Date:</span>{" "}
          {product.gazette}
        </p>

        <p className="mb-3 text-[15px]">
          <span className="font-semibold">2. Title of </span>
          <span className=" font-semibold">Biostimulant:</span>{" "}
          {product.title}
        </p>

        <p className="font-semibold mb-2 text-[15px]">3. Composition:</p>
        <div className="ml-6 mb-3 text-[15px]">
          {Object.entries(product.composition).map(([key, value], index) => (
            <p key={key} className="mb-1">
              ({toRoman(index + 1)}) <span className="font-semibold">{key}</span>: {value}
            </p>
          ))}
        </div>

        <p className="mb-3 text-[15px]">
          <span className="font-semibold">4. Crops:</span> {product.crops}
        </p>

        <p className="text-[15px] mb-3">
          <span className="font-semibold">5. Dosage:</span> {product.doses}
        </p>
        {
          product.table && (
            <div className="p-6 font-sans">
              <table className="border border-gray-500 border-collapse w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-500 px-3 py-2 text-left w-[10%]">S. No.</th>
                    <th className="border border-gray-500 px-3 py-2 text-left w-[30%]">Crop</th>
                    <th className="border border-gray-500 px-3 py-2 text-left">Dose</th>
                  </tr>
                </thead>
                <tbody>
                  {product?.table?.map((item) => (
                    <tr key={item.srNo}>
                      <td className="border border-gray-500 px-3 py-2 text-center">{item.srNo}</td>
                      <td className="border border-gray-500 px-3 py-2">{item.crop}</td>
                      <td className="border border-gray-500 px-3 py-2">{item.dose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }

        {product.drc && (
          <>
            <p className="font-semibold mb-2 text-[15px]">6. DRC No:</p>
            <div className="ml-6 mb-3 text-[15px]">
              <div className="mb-4">
                <label htmlFor="state-select" className="block mb-2 font-medium">Select State:</label>
                <select
                  id="state-select"
                  className="p-2 border border-black rounded font-sans"
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                >
                  <option value="">-- Choose state --</option>
                  {Object.keys(product.drc).map((code) => (
                    <option key={code} value={code}>
                      {stateNames[code] || code}
                    </option>
                  ))}
                </select>
              </div>

              {selectedState && (
                <div className="p-4 border border-black bg-gray-50">
                  <p className="font-bold underline">DRC Number:</p>
                  <p className="text-lg mt-1 font-mono">
                    {product.drc[selectedState] || "Registration in Process"}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default App;
